import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Building2, Pencil, Trash2, Users } from "lucide-react";

export default function Departments() {
  const [depts, setDepts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "" });
  const [delTarget, setDelTarget] = useState(null);

  const load = async () => setDepts((await api.get("/departments")).data);
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ name: "", description: "" }); setOpen(true); };
  const openEdit = (d) => { setEditing(d); setForm({ name: d.name, description: d.description || "" }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.put(`/departments/${editing.id}`, form);
      else await api.post("/departments", form);
      toast.success(editing ? "Department updated" : "Department created");
      setOpen(false); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const del = async () => {
    await api.delete(`/departments/${delTarget.id}`);
    toast.success("Department deleted");
    setDelTarget(null); load();
  };

  return (
    <>
      <PageHeader
        title="Departments"
        subtitle="Organise your team. Each department has its own tasks and targets."
        testId="departments-page"
        actions={
          <Button onClick={openNew} className="rounded-sm gap-2 font-bold uppercase text-xs tracking-wide" data-testid="add-department-button">
            <Plus className="h-4 w-4" /> Add Department
          </Button>
        }
      />

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {depts.map((d) => (
          <div key={d.id} className="border border-border bg-card rounded-sm p-5 hover:-translate-y-1 hover:hard-shadow transition-transform" data-testid={`department-${d.id}`}>
            <div className="flex items-start justify-between">
              <div className="h-10 w-10 bg-primary/10 text-primary flex items-center justify-center rounded-sm">
                <Building2 className="h-5 w-5" />
              </div>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" className="h-8 w-8 rounded-sm" onClick={() => openEdit(d)} data-testid={`edit-department-${d.id}`}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button size="icon" variant="ghost" className="h-8 w-8 rounded-sm text-destructive" onClick={() => setDelTarget(d)} data-testid={`delete-department-${d.id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <h3 className="font-display font-bold text-lg tracking-tight mt-4">{d.name}</h3>
            <p className="text-sm text-muted-foreground mt-1 line-clamp-2 min-h-[40px]">{d.description || "No description"}</p>
            <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1.5 border-t border-border pt-3">
              <Users className="h-3.5 w-3.5" /> {d.employee_count} employee{d.employee_count !== 1 ? "s" : ""}
            </p>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit Department" : "Add Department"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-sm" data-testid="department-name-input" /></div>
            <div><Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 rounded-sm" rows={3} /></div>
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={!form.name} className="rounded-sm" data-testid="save-department-button">
              {editing ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {delTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="rounded-sm bg-destructive hover:bg-destructive/90" data-testid="confirm-delete-department">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
